import {inject, injectable} from 'inversify';

import type {BaseDomainFactory} from '../Base/BaseDomain.factory';

import {ModelsRegistry} from '../../system/modelsRegistry/ModelsRegistry';
import {PrivateProfileFactory} from '../common/PrivateProfile/PrivateProfile.factory';
import {DateTimeModelFactory} from '../Date/DateTime.factory';
import {UserModel} from './User.model';
import {UserMetadataFactory} from './UserMetadata/UserMetadata.factory';
import {UserOnboardingFactory} from './UserOnboarding/UserOnboarding.factory';

@injectable()
export class UserFactory
  implements BaseDomainFactory<UserModel, UserModel.Props>
{
  constructor(
    @inject(PrivateProfileFactory)
    private readonly privateProfileFactory: PrivateProfileFactory,
    @inject(UserMetadataFactory)
    private readonly metadataFactory: UserMetadataFactory,
    @inject(UserOnboardingFactory)
    private readonly onboardingFactory: UserOnboardingFactory,
    @inject(DateTimeModelFactory)
    private readonly dateTimeModelFactory: DateTimeModelFactory,
    @inject(ModelsRegistry)
    private readonly modelsRegistry: ModelsRegistry,
  ) {}

  create(props: UserModel.Props): UserModel {
    return this.modelsRegistry.put(new UserModel(props));
  }

  createEmpty({name}: Pick<UserModel.Props, 'name'>): UserModel {
    return new UserModel({
      accountType: null,
      additionalData: null,
      apiData: {},
      avatar: null,
      coachLogo: null,
      coachMaxClients: 0,
      coachSubtenants: [],
      coachTitle: '',
      createdAt: this.dateTimeModelFactory.now(),
      description: '',
      externalId: null,
      gender: null,
      id: '',
      isCoach: false,
      isCorporateCoach: false,
      isDeleted: false,
      isSupport: false,
      lastSeenAt: null,
      location: null,
      metadata: this.metadataFactory.createEmpty(),
      name,
      onboarding: this.onboardingFactory.createEmpty(),
      populations: [],
      privateProfile: this.privateProfileFactory.createEmpty(),
      roles: [],
      slug: '',
      source: 'direct',
      subtenant: null,
      timezone: null,
      visible_widgets: [],
      yearOfBirth: null,
    });
  }
}
